import { Track } from 'livekit-client';
import * as React from 'react';
import { supportsScreenSharing } from '@livekit/components-core';
import {
  ChatIcon,
  ChatToggle,
  DisconnectButton,
  LeaveIcon,
  TrackToggle,
  useLocalParticipantPermissions,
  useMaybeLayoutContext,
  usePersistentUserChoices,
} from '@livekit/components-react';
import { Eye, X } from 'lucide-react';

import { notify } from 'gossamer-front-end';
import { MediaDeviceMenu } from '../MediaDeviceMenu/MediaDeviceMenu';
import { useLivestreamSessionContext } from '../../hooks';
import { RecordingButton } from '../RecordingButton/RecordingButton';
import { ParticipantsButton } from '../ParticipantsButton/ParticipantsButton';
import { StreamingButton } from '../StreamingButton/StreamingButton';
import { BreakoutRoomsButton } from '../BreakoutRoomsButton/BreakoutRoomsButton';
import classes from './ControlBar.module.css';

/** @public */
export type ControlBarControls = {
  microphone?: boolean;
  camera?: boolean;
  chat?: boolean;
  screenShare?: boolean;
  leave?: boolean;
  settings?: boolean;
  breakoutRooms?: boolean;
};

/** @public */
export interface ControlBarProps extends React.HTMLAttributes<HTMLDivElement> {
  onDeviceError?: (error: { source: Track.Source; error: Error }) => void;
  variation?: 'minimal' | 'verbose' | 'textOnly';
  controls?: ControlBarControls;
  /**
   * If `true`, the user's device choices will be persisted.
   * This will enable the user to have the same device choices when they rejoin the room.
   * @defaultValue true
   * @alpha
   */
  saveUserChoices?: boolean;
  viewCount: number;
}

/**
 * The `ControlBar` prefab gives the user the basic user interface to control their
 * media devices (camera, microphone and screen share), open the `Chat` and leave the room.
 *
 * @remarks
 * This component is build with other LiveKit components like `TrackToggle`,
 * `DeviceSelectorButton`, `DisconnectButton` and `StartAudio`.
 *
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <ControlBar />
 * </LiveKitRoom>
 * ```
 * @public
 */
export function ControlBar({ variation, controls, saveUserChoices = true, onDeviceError, viewCount }: ControlBarProps) {
  const {
    isPreview,
    handleEndLivestream,
    handleStartRecording,
    recordingProgressStatus,
    streamingProgressStatus,
    streamingStopFailed,
    hasStreamingDestinations,
    handleStartStreaming,
    handleStopStreaming,
  } = useLivestreamSessionContext();

  const [, setIsChatOpen] = React.useState(false);
  const layoutContext = useMaybeLayoutContext();
  React.useEffect(() => {
    if (layoutContext?.widget.state?.showChat !== undefined) {
      setIsChatOpen(layoutContext?.widget.state?.showChat);
    }
  }, [layoutContext?.widget.state?.showChat]);

  const defaultVariation = 'minimal';
  variation ??= defaultVariation;

  const visibleControls = { leave: true, ...controls };

  const localPermissions = useLocalParticipantPermissions();

  if (!localPermissions) {
    visibleControls.camera = false;
    visibleControls.chat = false;
    visibleControls.microphone = false;
    visibleControls.screenShare = false;
  } else {
    visibleControls.camera ??= localPermissions.canPublish;
    visibleControls.microphone ??= localPermissions.canPublish;
    visibleControls.screenShare ??= localPermissions.canPublish;
    visibleControls.chat ??= localPermissions.canPublishData && controls?.chat;
  }

  const showIcon = React.useMemo(() => variation === 'minimal' || variation === 'verbose', [variation]);
  const showText = React.useMemo(() => variation === 'textOnly' || variation === 'verbose', [variation]);

  const browserSupportsScreenSharing = supportsScreenSharing();

  const [isScreenShareEnabled, setIsScreenShareEnabled] = React.useState(false);

  const onScreenShareChange = React.useCallback(
    (enabled: boolean) => {
      setIsScreenShareEnabled(enabled);
      // if (!isPreview) handleStartRecording();
    },
    [setIsScreenShareEnabled, isPreview],
  );

  const { saveAudioInputEnabled, saveVideoInputEnabled, saveAudioInputDeviceId, saveVideoInputDeviceId } =
    usePersistentUserChoices({ preventSave: !saveUserChoices });

  const microphoneOnChange = React.useCallback(
    (enabled: boolean, isUserInitiated: boolean) => (isUserInitiated ? saveAudioInputEnabled(enabled) : null),
    [saveAudioInputEnabled],
  );

  const cameraOnChange = React.useCallback(
    (enabled: boolean, isUserInitiated: boolean) => (isUserInitiated ? saveVideoInputEnabled(enabled) : null),
    [saveVideoInputEnabled],
  );

  return (
    <div className={`lk-control-bar ${classes.controlBar}`}>
      {viewCount > 0 ? (
        <div className={classes.viewerCount}>
          <Eye size={20} />
          <span>
            {viewCount} viewer{viewCount > 1 && 's'}
          </span>
        </div>
      ) : (
        <div className={classes.viewerCount}>
          <Eye size={20} />
          <span>No viewers</span>
        </div>
      )}
      {visibleControls.microphone && (
        <div className="lk-button-group">
          <TrackToggle
            source={Track.Source.Microphone}
            showIcon={showIcon}
            onChange={microphoneOnChange}
            onDeviceError={(error) => onDeviceError?.({ source: Track.Source.Microphone, error })}
          >
            {showText && 'Microphone'}
          </TrackToggle>
          <div className="lk-button-group-menu">
            <MediaDeviceMenu
              exactMatch={true}
              kind="audioinput"
              onDeviceSelectError={() => {
                notify(['Audio input device change failed. Please try again.', 'danger']);
              }}
              onActiveDeviceChange={(_kind, deviceId) => {
                notify(['Your audio input has been changed.', 'info']);
                saveAudioInputDeviceId(deviceId ?? 'default');
              }}
            />
          </div>
        </div>
      )}
      {visibleControls.camera && (
        <div className="lk-button-group">
          <TrackToggle
            source={Track.Source.Camera}
            showIcon={showIcon}
            onChange={cameraOnChange}
            onDeviceError={(error) => onDeviceError?.({ source: Track.Source.Camera, error })}
          >
            {showText && 'Camera'}
          </TrackToggle>
          <div className="lk-button-group-menu">
            <MediaDeviceMenu
              exactMatch={true}
              kind="videoinput"
              requestPermissions={true}
              onDeviceSelectError={() => {
                notify(['Video input device change failed. Please try again.', 'danger']);
              }}
              onActiveDeviceChange={(_kind, deviceId) => {
                notify(['Your video input has been changed.', 'info']);
                saveVideoInputDeviceId(deviceId ?? 'default');
              }}
            />
          </div>
        </div>
      )}
      {visibleControls.screenShare && browserSupportsScreenSharing && (
        <TrackToggle
          source={Track.Source.ScreenShare}
          captureOptions={{ audio: true, selfBrowserSurface: 'include' }}
          showIcon={showIcon}
          onChange={onScreenShareChange}
          onDeviceError={(error) => onDeviceError?.({ source: Track.Source.ScreenShare, error })}
        >
          {isScreenShareEnabled ? 'Stop screen share' : 'Share screen'}
        </TrackToggle>
      )}
      {visibleControls.chat && (
        <ChatToggle>
          {showIcon && <ChatIcon />}
          {showText && 'Chat'}
        </ChatToggle>
      )}
      {!isPreview && (
        <div className={classes.controlGroup}>
          <ParticipantsButton />
          {visibleControls.breakoutRooms && <BreakoutRoomsButton />}
          <RecordingButton
            status={recordingProgressStatus}
            onStartRecording={handleStartRecording}
            // Add onStopRecording if you have a stop function
          />
          {hasStreamingDestinations && (
            <StreamingButton
              status={streamingProgressStatus}
              stopFailed={streamingStopFailed}
              onStartStreaming={handleStartStreaming}
              onStopStreaming={handleStopStreaming}
            />
          )}
        </div>
      )}
      <div className={classes.endControls}>
        {visibleControls.leave && (
          <DisconnectButton>
            {showIcon && <LeaveIcon />}
            Leave the room
          </DisconnectButton>
        )}
        <button type="button" className="lk-disconnect-button" onClick={handleEndLivestream}>
          <X size={20} />
          End the livestream
        </button>
      </div>
    </div>
  );
}
